import React, { useEffect, useState } from 'react';
import {
  type AddonSelection,
  type PlanPricing,
  addonLines,
  formatMoney,
  monthlyTotalCents,
} from '../../shared/plan';
import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  IconExternal,
  PlanName,
  Reason,
  type ReceiptLine,
  ReceiptLines,
  ReceiptSkeleton,
  ReceiptTotal,
} from '../../ui/v2';
import { type PreviewChange, formatDate } from './model';

export const TERMS_VERSION = 'v3.0';

/**
 * CheckoutPreview: the money confirmation for an in-place plan change. A
 * small dialog — the decision was made on the Plans page; this shows what
 * will be charged and asks once.
 *
 * Top to bottom: what changes → prorated lines → Due today (the one bold
 * number) → what happens next → agreement → one primary that names the
 * amount. Upgrades charge today; downgrades schedule for period end and
 * charge nothing, and the copy says so instead of showing $0 lines.
 */
export const CheckoutPreview: React.FC<{
  open: boolean;
  onClose: () => void;
  /** Current plan (name + code for the hue); `code` null when pricing is custom. */
  current: { code: string | null; name: string } | null;
  next: { code: string; name: string; pricing: PlanPricing };
  addons: AddonSelection;
  currency: string;
  tz: string;
  loadPreview: () => Promise<PreviewChange>;
  confirm: () => Promise<void>;
  /** Stripe portal, for when there is no card on file. */
  onOpenPortal: () => void;
  portalBusy?: boolean;
}> = ({
  open,
  onClose,
  current,
  next,
  addons,
  currency,
  tz,
  loadPreview,
  confirm,
  onOpenPortal,
  portalBusy,
}) => {
  const [preview, setPreview] = useState<PreviewChange | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setPreview(null);
    setLoadError(null);
    setError(null);
    setAgreed(false);
    loadPreview().then(
      (p) => alive && setPreview(p),
      (e: Error) => alive && setLoadError(e.message),
    );
    return () => {
      alive = false;
    };
  }, [open, attempt, loadPreview]);

  const monthly = monthlyTotalCents(next.pricing, addons);
  const upgrade = preview?.is_upgrade ?? true;
  const chargesToday = !!preview && upgrade && preview.total_cents > 0;
  // A warning, not a block: the preview names cards only, and a customer
  // paying another way must still be able to confirm.
  const noCard = !!preview && !preview.payment_method && chargesToday;
  const nextDate = preview?.next_payment_date
    ? formatDate(preview.next_payment_date, tz)
    : 'the next period';
  const card = preview?.payment_method
    ? `${preview.payment_method.brand} •••• ${preview.payment_method.last4}`
    : null;

  const lines: ReceiptLine[] = preview
    ? upgrade
      ? [
          {
            key: 'next',
            label: `${next.name} for the rest of this period`,
            sub: `prorated until ${nextDate}`,
            value: formatMoney(preview.subtotal_cents, currency, {
              exact: true,
            }),
          },
          ...(preview.adjustments_cents !== 0
            ? [
                {
                  key: 'credit',
                  label: current
                    ? `Credit for unused ${current.name}`
                    : 'Credit for unused time',
                  value: `−${formatMoney(Math.abs(preview.adjustments_cents), currency, { exact: true })}`,
                },
              ]
            : []),
        ]
      : [
          {
            key: 'base',
            label: `${next.name} base`,
            value: `${formatMoney(next.pricing.baseCents, currency)}/mo`,
          },
          ...addonLines(next.pricing, addons)
            .filter((a) => a.active)
            .map((a) => ({
              key: a.key,
              label: a.label,
              value: a.included
                ? 'Included'
                : `${formatMoney(a.cents, currency)}/mo`,
              muted: a.included,
            })),
        ]
    : [];

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await confirm();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const disabledReason = !preview
    ? 'Waiting for the price preview.'
    : !agreed
      ? 'Tick the agreement to continue.'
      : undefined;

  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      size="sm"
      title={`Change to ${next.name}`}
      description={
        current
          ? `From ${current.name}${upgrade ? ' — takes effect today.' : ` — takes effect on ${nextDate}.`}`
          : 'Replaces your agreed pricing.'
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Keep current plan
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={busy}
            disabledReason={disabledReason}
            onClick={submit}
          >
            {chargesToday && preview
              ? `Confirm and pay ${formatMoney(preview.total_cents, currency, { exact: true })}`
              : 'Confirm change'}
          </Button>
        </>
      }
    >
      <div className="mb-3 flex items-center gap-3 text-v2-13">
        {current && (
          <>
            <PlanName code={current.code} name={current.name} size="sm" />
            <span aria-hidden className="text-v2-fg-4">
              →
            </span>
          </>
        )}
        <PlanName code={next.code} name={next.name} size="sm" />
      </div>

      {loadError ? (
        <Callout tone="danger" title="The price preview could not be loaded.">
          {loadError}
          <div className="mt-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setAttempt((a) => a + 1)}
            >
              Retry
            </Button>
          </div>
        </Callout>
      ) : !preview ? (
        <ReceiptSkeleton rows={3} />
      ) : (
        <>
          <ReceiptLines lines={lines} />
          <ReceiptTotal
            className="mt-1"
            label={upgrade ? 'Due today' : `From ${nextDate}`}
            value={
              upgrade
                ? formatMoney(preview.total_cents, currency, { exact: true })
                : `${formatMoney(monthly, currency)}/mo`
            }
            note={
              upgrade ? (
                <>
                  Then {formatMoney(monthly, currency)}/mo from {nextDate}
                  {card ? `, charged to ${card}` : ''}. Tax is added by Stripe.
                </>
              ) : (
                <>
                  Nothing is charged today. You keep{' '}
                  {current?.name ?? 'your current plan'}’s minutes until{' '}
                  {nextDate}, then {next.name} starts
                  {card ? ` on ${card}` : ''}.
                </>
              )
            }
          />
          {noCard && (
            <Callout tone="warning" title="No card on file." className="mt-3">
              Stripe will try the default payment method on the account. To add
              a card first, open the Stripe portal.
              <div className="mt-2">
                <Button
                  size="sm"
                  variant="secondary"
                  trailing={<IconExternal size={12} />}
                  loading={portalBusy}
                  onClick={onOpenPortal}
                >
                  Add payment method
                </Button>
              </div>
            </Callout>
          )}
          <div className="mt-4">
            <Checkbox
              label="I authorize the displayed plan change and charges under my existing agreement."
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="items-start [&>input]:mt-0.5"
            />
            <Reason className="mt-1 pl-6">
              Prices exclude tax.{' '}
              <a
                href="/legal/business"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                Business agreement documents
              </a>
              . This change does not adopt a new agreement version.
            </Reason>
          </div>
          {error && (
            <Callout tone="danger" className="mt-3">
              {error}
            </Callout>
          )}
        </>
      )}
    </Dialog>
  );
};

export default CheckoutPreview;
